import {
  Users,
  IdentificationBadge,
  ClockClockwise,
  IdentificationCard,
} from '@phosphor-icons/react'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'

export const VistorModalTypes = {
  VisitorTypeModal: 'VisitorTypeModal',
  GuestTypeModal: 'GuestTypeModal',
  VendorTypeModal: 'VendorTypeModal',
  OneTimeGuest: 'OneTimeGuest',
  RecurringGuest: 'RecurringGuest',
  OneTimeVendor: 'OneTimeVendor',
  RecurringVendor: 'RecurringVendor',
}

const iconSize = 40
const MINUTE_IN_MILISECONDS = 60 * 1000
const MIN_VISIT_DURATION = 15

export const getVisitorTypeModalConfig = (t, type) => {
  const newVisitorTypeConfig = {
    header: t('visitors:modalTypes.addNewVisitor'),
    subHeader: t('visitors:modalTypes.addNewVisitorSubHeader'),
    options: [
      {
        icon: <Users size={iconSize} />,
        text: t('visitors:modalTypes.guest'),
        value: VistorModalTypes.GuestTypeModal,
      },
      {
        icon: <IdentificationBadge size={iconSize} />,
        text: t('visitors:modalTypes.vendor'),
        value: VistorModalTypes.VendorTypeModal,
      },
    ],
  }

  const newGuestTypeConfig = {
    header: t('visitors:modalTypes.addNewGuest'),
    subHeader: t('visitors:modalTypes.addNewGuestSubHeader'),
    options: [
      {
        icon: <ClockClockwise size={iconSize} />,
        text: t('visitors:modalTypes.oneTimeGuest'),
        value: VistorModalTypes.OneTimeGuest,
      },
      {
        icon: <IdentificationCard size={iconSize} />,
        text: t('visitors:modalTypes.recurringGuestPermissions'),
        value: VistorModalTypes.RecurringGuest,
      },
    ],
  }

  const newVendorTypeConfig = {
    header: t('visitors:modalTypes.addNewVendor'),
    subHeader: t('visitors:modalTypes.addNewVendorSubHeader'),
    options: [
      {
        icon: <ClockClockwise size={iconSize} />,
        text: t('visitors:modalTypes.oneTimeVendor'),
        value: VistorModalTypes.OneTimeVendor,
      },
      {
        icon: <IdentificationCard size={iconSize} />,
        text: t('visitors:modalTypes.recurringVendorPermissions'),
        value: VistorModalTypes.RecurringVendor,
      },
    ],
  }

  switch (type) {
    case VistorModalTypes.VisitorTypeModal:
      return newVisitorTypeConfig
    case VistorModalTypes.GuestTypeModal:
      return newGuestTypeConfig
    case VistorModalTypes.VendorTypeModal:
      return newVendorTypeConfig
  }
}

export const getIsVisitorTypeModalOpen = (modalType) =>
  [
    VistorModalTypes.VisitorTypeModal,
    VistorModalTypes.GuestTypeModal,
    VistorModalTypes.VendorTypeModal,
  ].includes(modalType)

export const getIsVisitorFormModalOpen = (modalType) =>
  [
    VistorModalTypes.OneTimeGuest,
    VistorModalTypes.RecurringGuest,
    VistorModalTypes.OneTimeVendor,
    VistorModalTypes.RecurringVendor,
  ].includes(modalType)

// `text` stays untranslated: it is persisted as the visitor's `type` value (see
// mapValuesToGuestData) and matched against server data, not just display copy.
// Use `getVisitorTypeOptions` for translated labels shown in selects.
export const visitorTypes = [
  { text: 'Friend', id: 'Friend' },
  { text: 'Relative', id: 'relative' },
  { text: 'Personal Trainer', id: 'personalTrainer' },
  { text: 'Housekeeper', id: 'housekeeper' },
  { text: 'Handyman', id: 'handyman' },
  { text: 'Dog Walker', id: 'dogWalker' },
  { text: 'Other', id: 'other' },
]

export const getVisitorTypeOptions = (t) => [
  { value: 'Friend', label: t('visitors:recipientTypes.friend') },
  { value: 'relative', label: t('visitors:recipientTypes.relative') },
  { value: 'personalTrainer', label: t('visitors:recipientTypes.personalTrainer') },
  { value: 'housekeeper', label: t('visitors:recipientTypes.housekeeper') },
  { value: 'handyman', label: t('visitors:recipientTypes.handyman') },
  { value: 'dogWalker', label: t('visitors:recipientTypes.dogWalker') },
  { value: 'other', label: t('visitors:recipientTypes.other') },
]

// `id` is the only part persisted (see mapValuesTo*Data appending `mode`), so `text`
// can be translated directly.
export const getPermissionTypes = (t) => [
  { text: t('visitors:permissions.oneTime'), id: 'custom' },
  { text: t('visitors:permissions.daily'), id: 'daily' },
  { text: t('visitors:permissions.weekly'), id: 'weekly' },
  { text: t('visitors:permissions.monthly'), id: 'monthly' },
]

export const visitorDetailsType = {
  Guest: 'guest',
  Vendor: 'vendor',
}

export const getVisitorFormModalType = (visitor) => {
  const isRecurring = ['daily', 'weekly', 'monthly'].includes(visitor?.mode)

  return isRecurring ? VistorModalTypes.RecurringGuest : VistorModalTypes.OneTimeGuest
}

export const mapGuestDetailsToFormValues = (t, data, buildingTimezone) => {
  const visitor = data?.visitor || {}
  const startTime = visitor.startTime
    ? toZonedTime(new Date(visitor.startTime), buildingTimezone)
    : null
  const endTime = visitor.endTime ? toZonedTime(new Date(visitor.endTime), buildingTimezone) : null
  const residentName = visitor.residentName || ''
  const unit = visitor.unitNumber || visitor.unit || ''
  const visitorType = visitorTypes.find(
    (type) =>
      type.id.toLowerCase() === `${visitor.type || ''}`.trim().toLowerCase() ||
      type.text.toLowerCase() === `${visitor.type || ''}`.trim().toLowerCase(),
  )
  const selectedDays = Array.isArray(visitor.periodicity)
    ? visitor.periodicity.map(Number).filter((day) => !Number.isNaN(day))
    : []

  return {
    resident: visitor.clientId
      ? {
          value: visitor.clientId,
          label: [residentName, unit ? `#${unit}` : ''].filter(Boolean).join(' '),
        }
      : '',
    clientId: visitor.clientId || '',
    visitorName: visitor.name || '',
    visitorPhone: visitor.phone ? String(visitor.phone) : '',
    visitorEmail: visitor.email || '',
    visitorCompany: visitor.company || '',
    coi: null,
    visitorType: visitorType || { id: '', text: visitor.type || '' },
    permissionType:
      getPermissionTypes(t).find((type) => type.id === visitor.mode) || getPermissionTypes(t)[0],
    visitorId: `${visitor.guestId || ''}`,
    timeStart: startTime,
    timeEnd: endTime,
    dueDate: startTime,
    visitorNote: visitor.notes || '',
    isVisitorNoteOpen: !!visitor.notes,
    selectedDays,
    monthlyPermission: visitor.monthlyPermission || '',
    keyAccess: !!visitor.keyAccess,
    keylessAccess: !!visitor.keylessAccess,
    customAccess: !!visitor.customAccess,
    pinCode: !!visitor.pinCode,
    startOfDay: startTime,
    endOfDay: endTime,
  }
}

export const calculateMonthlyPattern = (t, selectedDate) => {
  const dayOfWeek = selectedDate.toLocaleDateString('en-US', { weekday: 'long' })
  let weekOfMonth = Math.ceil(selectedDate.getDate() / 7)
  switch (weekOfMonth) {
    case 1:
      weekOfMonth = t('visitors:monthlyPattern.first')
      break
    case 2:
      weekOfMonth = t('visitors:monthlyPattern.second')
      break
    case 3:
      weekOfMonth = t('visitors:monthlyPattern.third')
      break
    case 4:
      weekOfMonth = t('visitors:monthlyPattern.fourth')
      break
    case 5:
      weekOfMonth = t('visitors:monthlyPattern.fifth')
      break
  }
  return t('visitors:monthlyPattern.everyDayOfMonth', { weekOfMonth, dayOfWeek })
}

const mergeDateAndTime = (datePart, timePart) => {
  const newDate = new Date(datePart)

  newDate.setHours(
    timePart.getHours(),
    timePart.getMinutes(),
    timePart.getSeconds(),
    timePart.getMilliseconds(),
  )

  return newDate
}

const getStartAndEndTime = (values, buildingTimezone) => {
  const dueDate = values.dueDate ? new Date(values.dueDate) : new Date()
  const timeStart = values.timeStart ? new Date(values.timeStart) : new Date()
  let timeEnd = values.timeEnd ? new Date(values.timeEnd) : new Date()

  // Note: time end has to be at least 15 minutes after time start
  if (timeEnd.getTime() - timeStart.getTime() < MIN_VISIT_DURATION * MINUTE_IN_MILISECONDS) {
    timeEnd = new Date(timeStart.getTime() + MIN_VISIT_DURATION * MINUTE_IN_MILISECONDS)
  }

  const startDateTime =
    values.permissionType.id === 'custom' && !!values.keyAccess
      ? timeStart
      : mergeDateAndTime(dueDate, timeStart)
  const endDateTime =
    values.permissionType.id === 'custom' && !!values.keyAccess
      ? timeEnd
      : mergeDateAndTime(dueDate, timeEnd)

  // Note: Convert from building timezone to UTC for the API
  const newTimeStart = fromZonedTime(startDateTime, buildingTimezone).toISOString()
  const newTimeEnd = fromZonedTime(endDateTime, buildingTimezone).toISOString()

  return { newTimeStart, newTimeEnd }
}

export const mapValuesToGuestData = (values, isRecurringType, buildingTimezone) => {
  const clientId = values.resident ? values.resident.value : null

  let newTimeStart, newTimeEnd

  if (!isRecurringType) {
    const guestTimeRange = getStartAndEndTime(values, buildingTimezone)

    newTimeStart = guestTimeRange.newTimeStart
    newTimeEnd = guestTimeRange.newTimeEnd
  } else {
    if (values.keyAccess) {
      // Note: When keyAccess is true, timeStart and timeEnd already contain full date ranges
      // Convert from building timezone to UTC
      newTimeStart = fromZonedTime(new Date(values.timeStart), buildingTimezone).toISOString()
      newTimeEnd = fromZonedTime(new Date(values.timeEnd), buildingTimezone).toISOString()
    } else {
      // Note: When keyAccess is false, merge the selected date with the time portions
      const dueDate = new Date(values.dueDate)
      const timeStart = new Date(values.timeStart)
      const timeEnd = new Date(values.timeEnd)

      // Note: Merge date and time, then convert from building timezone to UTC
      newTimeStart = fromZonedTime(
        mergeDateAndTime(dueDate, timeStart),
        buildingTimezone,
      ).toISOString()
      newTimeEnd = fromZonedTime(mergeDateAndTime(dueDate, timeEnd), buildingTimezone).toISOString()
    }
  }

  const formData = new FormData()

  formData.append('name', values.visitorName)
  formData.append('email', values.visitorEmail)
  formData.append('company', values.visitorCompany)
  formData.append('mode', values.permissionType.id)
  formData.append('startTime', newTimeStart)
  formData.append('endTime', newTimeEnd)
  formData.append('keylessAccess', values.keylessAccess)
  formData.append('pinCode', values.pinCode)
  formData.append('customAccess', values.customAccess)
  // Note: keyAccess is the one generic BLE flag (EL-12922) — the form has a single "Keyless
  // Access" toggle, routing to whichever provider the building has enabled.
  formData.append('keyAccess', values.keyAccess)
  formData.append('periodicity', `[${values.selectedDays}]`)
  formData.append('type', values.visitorType.text || 'Other')
  formData.append('notes', values.isVisitorNoteOpen ? values.visitorNote : '')

  if (values.visitorPhone) {
    formData.append('phone', parseInt(values.visitorPhone))
  }

  if (values.visitorId) {
    formData.append('guestId', parseInt(values.visitorId))
  }

  if (isRecurringType) {
    formData.append('monthlyPermission', values.monthlyPermission || 'Other')
  }

  return { clientId, formData }
}

export const mapValuesToVendorData = (values, isRecurringType, buildingId, buildingTimezone) => {
  const { newTimeStart, newTimeEnd } = getStartAndEndTime(values, buildingTimezone)

  const formData = new FormData()

  formData.append('name', values.visitorName)
  formData.append('email', values.visitorEmail)
  formData.append('company', values.visitorCompany)
  formData.append('buildingId', buildingId)
  formData.append('mode', values.permissionType.id)
  formData.append('startTime', newTimeStart)
  formData.append('endTime', newTimeEnd)
  formData.append('periodicity', `[${values.selectedDays}]`)
  formData.append('notes', values.isVisitorNoteOpen ? values.visitorNote : '')
  formData.append('keylessAccess', values.keylessAccess)
  formData.append('customAccess', values.customAccess)
  formData.append('pinCode', values.pinCode)
  // Note: see mapValuesToGuestData — keyAccess is the one generic BLE flag (EL-12922).
  formData.append('keyAccess', values.keyAccess)

  if (values.visitorPhone) {
    formData.append('phone', parseInt(values.visitorPhone))
  }

  if (values.visitorId) {
    formData.append('vendorId', parseInt(values.visitorId))
  }

  if (values.resident) {
    formData.append('clientId', values.resident.value)
  }

  if (isRecurringType) {
    formData.append('monthlyPermission', values.monthlyPermission)
  }

  // Note: COI has to be added as a last field to avoid issues with the backend
  if (values.coi) {
    formData.append('coi', values.coi)
  }

  return formData
}
